import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Search } from "lucide-react";
import debounce from "lodash/debounce";
import Pagination from "./Paginate";
import DateRangePicker from './DateRangePicker';
import {
  fetchPayments,
  setFilter,
  resetFilters,
} from "../store/slices/paymentsSlice";
import PaymentTable from "../Table/PaymentTable";
import LoadingSpinner from "./LoadingSpinner";

const PaymentListing = ({loanId}) => {
  const dispatch = useDispatch();
  const { payments, pagination, loading, filters } = useSelector(
    (state) => state.payments
  );
  const [localSearch, setLocalSearch] = useState("");
  const [isInitialized, setIsInitialized] = useState(false);

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        dispatch(setFilter({ search: value, page: 1 }));
      }, 500),
    [dispatch]
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useEffect(() => {
    const initializeFilters = () => {
      const defaultFilters = {
        page: 1,
        limit: 10,
        search: "",
        loanId: loanId || "",
        paymentType: "",
        status: "",
        sortBy: "createdAt",
        sortOrder: "desc",
        startDate: null,
        endDate: null
      };
      
      dispatch(setFilter(defaultFilters));
      setIsInitialized(true);
    };

    initializeFilters();
  }, []);

  useEffect(() => {
    if (!isInitialized) return;
    dispatch(fetchPayments());
  }, [filters, isInitialized]);

  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value;
      setLocalSearch(value);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  const handlePaymentTypeChange = (e) => {
    dispatch(setFilter({ paymentType: e.target.value, page: 1 }));
  };

  const handleStatusChange = (e) => {
    dispatch(setFilter({ status: e.target.value, page: 1 }));
  };

  const handleDateChange = (dates) => {
    const { startDate, endDate } = dates;
    dispatch(setFilter({
      startDate: startDate ? startDate.toISOString().split('T')[0] : null,
      endDate: endDate ? endDate.toISOString().split('T')[0] : null,
      page: 1
    }));
  };

  const handleClearFilters = useCallback(() => {
    dispatch(resetFilters());
    setLocalSearch("");
    debouncedSearch.cancel();
  }, [dispatch, debouncedSearch]);

  const dateRangeValue = {
    startDate: filters.startDate ? new Date(filters.startDate) : null,
    endDate: filters.endDate ? new Date(filters.endDate) : null
  };

  if(loading){
    return <LoadingSpinner />
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold">Payments</h1>
        {!loanId && <button
          onClick={handleClearFilters}
          className="px-4 py-2 border border-gray-200 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors"
        >
          Clear Filters
        </button>}
      </div>

      {!loanId && <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search payments..."
            value={localSearch}
            onChange={handleSearchChange}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div className="w-full sm:w-[200px]">
          <select
            value={filters.paymentType}
            onChange={handlePaymentTypeChange}
            className="w-full px-4 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Types</option>
            <option value="CREDIT">Credit</option>
            <option value="DEBIT">Debit</option>
          </select>
        </div>
        <div className="w-full sm:w-[200px]">
          <select
            value={filters.status}
            onChange={handleStatusChange}
            className="w-full px-4 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Status</option>
            <option value="SUCCESS">Success</option>
            <option value="PENDING">Pending</option>
            <option value="FAILED">Failed</option>
          </select>
        </div>
        <div className="w-full sm:w-[300px]">
          <DateRangePicker
            startDate={dateRangeValue.startDate}
            endDate={dateRangeValue.endDate}
            onChange={handleDateChange}
          />
        </div>
      </div>}

      <div className="rounded-lg border border-gray-200 overflow-hidden">
        <PaymentTable
          payments={payments}
          loading={loading}
          filters={filters}
        />
      </div>

      {!loading && payments?.length > 0 && (
        <Pagination
          currentPage={filters.page}
          totalItems={pagination.totalItems}
          pageSize={filters.limit}
          onPageChange={(page) => dispatch(setFilter({ page }))}
          onPageSizeChange={(limit) => dispatch(setFilter({ limit, page: 1 }))}
        />
      )}
    </div>
  );
};

export default PaymentListing;